import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';

// Any parameter scoring below this is considered "needs attention."
// A vendor can trigger multiple panels at once (e.g. weak on both
// Delivery and Quality) instead of only ever showing its single worst score.
// NOTE: raised from 70 -> 85. Real-world "needs attention" vendors were
// scoring in the 80s (e.g. Delivery Score 81.40/100), so a 70 cutoff was
// letting genuinely risky vendors show as "Performing Well."
const ATTENTION_THRESHOLD = 85;

// Central config for each evaluation parameter — icon, label, and how to
// build its "Reason" bullets from whatever raw fields are actually present
// on the vendor record. getReasons always receives the mapped activity so
// it can look for optional fields that may or may not exist.
const ATTENTION_CONFIG = {
  Delivery: {
    icon: '🚚',
    label: 'Delivery Performance',
    scoreKey: 'deliveryScore',
    getReasons: (a) => [
      `Average delivery delay is ${a.avgDelay.toFixed(2)} days.`,
      `Maximum delay reached ${a.maxDelay} days.`,
      `${a.lateDeliveries} deliveries were late.`,
      `On-time delivery performance is only ${a.onTime.toFixed(2)}%.`,
    ],
    impact: [
      'Production schedules may be delayed.',
      'Material shortages may occur.',
      'Customer deliveries may be delayed.',
      'Inventory planning becomes unreliable.',
    ],
  },
  Quality: {
    icon: '🛠️',
    label: 'Quality Performance',
    scoreKey: 'qualityScore',
    getReasons: (a) => [
      `Rework percentage is ${a.rework.toFixed(2)}%.`,
      `Defective quantity is ${a.defectiveQty}.`,
      'Product quality consistency has declined.',
    ],
    impact: [
      'Production defects may increase.',
      'Additional inspection effort is required.',
      'Rework cost increases.',
      'Customer complaints may increase.',
    ],
  },
  Procurement: {
    icon: '📦',
    label: 'Procurement Performance',
    scoreKey: 'procurementScore',
    // poConversionRate / procurementCycleDays only exist if the API
    // returns them. If not, fall back to a generic statement instead of
    // inventing numbers.
    getReasons: (a) => {
      const reasons = [];
      reasons.push(
        a.poConversionRate !== null
          ? `Purchase Order conversion rate is ${a.poConversionRate.toFixed(2)}%.`
          : 'Purchase Order conversion efficiency is low.'
      );
      reasons.push(
        a.procurementCycleDays !== null
          ? `Average procurement cycle time is ${a.procurementCycleDays.toFixed(1)} days.`
          : 'Procurement cycle performance has reduced.'
      );
      reasons.push('Material procurement requires attention.');
      return reasons;
    },
    impact: [
      'Material shortages may occur.',
      'Production planning may be delayed.',
      'Procurement lead time increases.',
    ],
  },
  Financial: {
    icon: '💰',
    label: 'Financial Performance',
    scoreKey: 'financialScore',
    // paymentDelayDays / creditScore only exist if the API returns them.
    // If not, fall back to a generic statement.
    getReasons: (a) => {
      const reasons = [];
      reasons.push(
        a.paymentDelayDays !== null
          ? `Average payment delay is ${a.paymentDelayDays.toFixed(1)} days.`
          : 'Financial stability is below target.'
      );
      reasons.push(
        a.creditScore !== null
          ? `Credit score stands at ${a.creditScore}.`
          : 'Payment performance requires monitoring.'
      );
      reasons.push('Vendor financial risk has increased.');
      return reasons;
    },
    impact: [
      'Possible future supply disruption.',
      'Delayed deliveries.',
      'Higher procurement risk.',
    ],
  },
};

// Priority reflects both the vendor's stated risk level and how many
// parameters are underperforming — a vendor weak in 3 areas outranks one
// weak in only 1, even if Risk_Level wasn't set correctly upstream.
function derivePriority(activity) {

  // DELIVERY
  if (activity.worstType === "Delivery") {

    if (
        activity.avgDelay >= 2 ||
        activity.maxDelay >= 90 ||
        activity.lateDeliveries >= 100 ||
        activity.onTime <= 60
    ) {
        return "High";
    }

    if (activity.avgDelay < 0) {
        return "Medium";
    }

    if (activity.worstScore < 85) {
        return "Medium";
    }

    return "Low";
}

  // Use "contains" matching, not exact equality — real API values are
  // often "High Risk", "Critical", "High - Delivery", etc., not a bare
  // "High" string. Exact-equality here was the reason High Priority KPI
  // showed 0 even when vendors clearly had a high-risk label.
  const isHigh = normalized.includes('high') || normalized.includes('critical') || normalized.includes('severe');
  const isMedium = normalized.includes('medium') || normalized.includes('moderate');
  const isLow = normalized.includes('low');

  if (underperformingCount >= 2 || worstScore < 50 || isHigh) return 'High';
  if (underperformingCount === 1 || isMedium) return 'Medium';
  if (isLow && underperformingCount === 0) return 'Low';

  return worstScore < ATTENTION_THRESHOLD ? 'Medium' : 'Low';
}

const PRIORITY_RANK = { High: 0, Medium: 1, Low: 2 };

// Reads an optional numeric field from the raw row, trying a few common
// key spellings. Returns null (not 0) when the field truly isn't present,
// so getReasons can tell "missing" apart from "actually zero."
function optionalNumber(vendor, keys) {
  for (const key of keys) {
    if (vendor[key] !== undefined && vendor[key] !== null && vendor[key] !== '') {
      const n = Number(vendor[key]);
      if (!Number.isNaN(n)) return n;
    }
  }
  return null;
}

export default function VendorActivity() {
  const navigate = useNavigate();

  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadVendorRisk();
  }, []);

  const loadVendorRisk = async () => {
    try {
      setError(null);
      const data = await api.get('/vendors/risk');
      const vendorRows = data.rows || [];

      const alerts = vendorRows.map((vendor) => {
        const vendorName =
          vendor.vendor_name ||
          vendor.Vendor ||
          vendor['Vendor Name'] ||
          'Unknown Vendor';

        const vendorCategory =
          vendor.Vendor_Category ||
          vendor.vendor_category ||
          vendor['Vendor Category'] ||
          'Uncategorized';

        const deliveryScore = Number(vendor.Delivery_Score || 0);
        const qualityScore = Number(vendor.Quality_Score || 0);
        const procurementScore = Number(vendor.Procurement_Score || 0);
        const financialScore = Number(vendor.Financial_Score || 0);

        const scores = [
          { type: 'Delivery', score: deliveryScore },
          { type: 'Quality', score: qualityScore },
          { type: 'Procurement', score: procurementScore },
          { type: 'Financial', score: financialScore },
        ];

        const sortedScores = [...scores].sort((a, b) => a.score - b.score);
        const worst = sortedScores[0];

        let underperforming = scores
          .filter((s) => s.score < ATTENTION_THRESHOLD)
          .sort((a, b) => a.score - b.score)
          .map((s) => s.type);

        const riskLevel =
          vendor.Risk_Level || vendor.RiskLevel || vendor['Risk Level'] || 'Unknown';
        const riskLevelNormalized = riskLevel.toString().trim().toLowerCase();
        const apiFlaggedHigh =
          riskLevelNormalized.includes('high') ||
          riskLevelNormalized.includes('critical') ||
          riskLevelNormalized.includes('severe');

        // If the API itself marked this vendor High/Critical risk but every
        // individual score happened to land just above our threshold, still
        // surface the weakest parameter instead of showing "Performing Well" —
        // the API's own risk call takes precedence over the local cutoff.
        if (apiFlaggedHigh && underperforming.length === 0) {
          underperforming = [worst.type];
        }

        const priority = derivePriority({

  riskLevel,

  worstType: worst.type,

  worstScore: worst.score,

  avgDelay: Number(vendor.Avg_Delay_Days || 0),

  maxDelay: Number(vendor.Max_Delay_Days || 0),

  lateDeliveries: Number(vendor.Late_Deliveries || 0),

  onTime: Number(vendor.On_Time_Percentage || 0),

  rework: Number(vendor.Rework_Percentage || 0),

  defectiveQty: Number(vendor.Defective_Qty || 0)

});

        return {
          vendorName,
          vendorCategory,
          vendorRating: Number(vendor.Vendor_Rating || 0),
          riskLevel,
          priority,

          deliveryScore,
          qualityScore,
          procurementScore,
          financialScore,

          avgDelay: Number(vendor.Avg_Delay_Days || 0),
          maxDelay: Number(vendor.Max_Delay_Days || 0),
          lateDeliveries: Number(vendor.Late_Deliveries || 0),
          onTime: Number(vendor.On_Time_Percentage || 0),
          rework: Number(vendor.Rework_Percentage || 0),
          defectiveQty: Number(vendor.Defective_Qty || 0),

          // Optional fields — null when the API doesn't provide them.
          poConversionRate: optionalNumber(vendor, ['PO_Conversion_Rate', 'Po_Conversion_Rate']),
          procurementCycleDays: optionalNumber(vendor, ['Procurement_Cycle_Days']),
          paymentDelayDays: optionalNumber(vendor, ['Payment_Delay_Days']),
          creditScore: optionalNumber(vendor, ['Credit_Score']),

          worstType: worst.type,
          worstScore: worst.score,
          underperforming, // e.g. ["Quality", "Delivery"], sorted worst-first

          activityType:
            underperforming.length > 1
              ? 'Multiple Risk Areas'
              : underperforming.length === 1
              ? `${underperforming[0]} Risk Alert`
              : 'Performing Well',
          icon:
            underperforming.length > 0
              ? ATTENTION_CONFIG[worst.type]?.icon || '⚠️'
              : '✅',
        };
      });

      // Surface the most urgent vendors first: High > Medium > Low,
      // and within the same priority, weakest score first.
      alerts.sort((a, b) => {
        const rankDiff = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
        if (rankDiff !== 0) return rankDiff;
        return a.worstScore - b.worstScore;
      });

      setActivities(alerts);
    } catch (err) {
      console.error('Vendor Activity Error:', err);
      setError('Unable to load vendor activities. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        Loading Vendor Activities...
      </div>
    );
  }

  const highPriorityCount = activities.filter((a) => a.priority === 'High').length;
  const attentionCount = activities.filter((a) => a.underperforming.length > 0).length;

  return (
    <div className="p-6 space-y-6">

      {/* HEADER */}
      <div>
        <h1 className="text-2xl font-bold text-sap-text">
          Vendor Activity Center
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Overview of vendor activities requiring attention across Delivery, Quality, Procurement, and Financial performance.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">
          {error}
        </div>
      )}

      {/* KPI CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">

        <div className="bg-white rounded-xl shadow border p-4">
          <div className="text-xs text-gray-500">Total Activities</div>
          <div className="text-3xl font-bold">{activities.length}</div>
        </div>

        <div className="bg-red-50 rounded-xl shadow border p-4">
          <div className="text-xs text-gray-500">High Priority</div>
          <div className="text-3xl font-bold text-red-600">{highPriorityCount}</div>
        </div>

        <div className="bg-orange-50 rounded-xl shadow border p-4">
          <div className="text-xs text-gray-500">Needs Attention</div>
          <div className="text-3xl font-bold text-orange-600">{attentionCount}</div>
        </div>

        <div className="bg-blue-50 rounded-xl shadow border p-4">
          <div className="text-xs text-gray-500">Total Vendors</div>
          <div className="text-3xl font-bold text-blue-600">{activities.length}</div>
        </div>

      </div>

      {/* NO DATA */}
      {activities.length === 0 ? (
        <div className="bg-green-100 border border-green-300 rounded-lg p-4">
          ✅ No vendor activities requiring attention.
        </div>
      ) : (
        <div className="space-y-4">
          {activities.map((activity, index) => (
            <div
              key={`${activity.vendorName}-${index}`}
              className="bg-white border border-gray-200 rounded-xl shadow-sm p-4"
            >
              <div className="flex justify-between items-start">

                <div className="flex-1">

                  <h3 className="font-bold text-lg">
                    {activity.icon} {activity.activityType}
                  </h3>

                  <h3 className="text-xl font-bold mt-1">
                    {activity.vendorName}
                  </h3>

                  <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">

                    <div className="border rounded-lg p-3">
                      <div className="text-xs text-gray-500">Vendor Rating</div>
                      <div className="font-bold">{activity.vendorRating.toFixed(2)}</div>
                    </div>

                    <div className="border rounded-lg p-3">
                      <div className="text-xs text-gray-500">Category</div>
                      <div className="font-bold">{activity.vendorCategory}</div>
                    </div>

                    <div className="border rounded-lg p-3">
                      <div className="text-xs text-gray-500">Risk Level</div>
                      <div className="font-bold text-red-600">{activity.riskLevel}</div>
                    </div>

                    <div className="border rounded-lg p-3">
                      <div className="text-xs text-gray-500">Weakest Area</div>
                      <div className="font-bold">{activity.worstType}</div>
                      <div className="text-sm text-gray-600">
                        {activity.worstScore.toFixed(2)}/100
                      </div>
                    </div>

                  </div>

                  {/* All 4 parameter scores, always shown; weak ones highlighted */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">

                    {['Delivery', 'Quality', 'Procurement', 'Financial'].map((type) => {
                      const scoreKey = ATTENTION_CONFIG[type].scoreKey;
                      const score = activity[scoreKey];
                      const isWeak = activity.underperforming.includes(type);
                      return (
                        <div
                          key={type}
                          className={`border rounded-lg p-3 ${
                            isWeak ? 'border-red-300 bg-red-50' : ''
                          }`}
                        >
                          <b>{type}</b>
                          <div className={isWeak ? 'text-red-600 font-semibold' : ''}>
                            {score.toFixed(2)}/100
                          </div>
                        </div>
                      );
                    })}

                  </div>

                  {/* ================= NEEDS IMMEDIATE ATTENTION ================= */}
                  {activity.underperforming.length > 0 ? (
                    <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-5 space-y-6">

                      <h3 className="text-lg font-bold text-red-700">
                        ⚠ Needs Immediate Attention
                      </h3>

                      {/* One panel per underperforming parameter, worst first */}
                      {activity.underperforming.map((type) => {
                        const config = ATTENTION_CONFIG[type];
                        const score = activity[config.scoreKey];
                        return (
                          <div key={type} className="space-y-3">

                            <h4 className="text-lg font-semibold">
                              {config.icon} {config.label}
                            </h4>

                            <div className="text-xl font-bold text-red-600">
                              {type} Score : {score.toFixed(2)} /100
                            </div>

                            <div>
                              <h5 className="font-semibold">Reason</h5>
                              <ul className="list-disc ml-6 mt-2 space-y-1 text-sm">
                                {config.getReasons(activity).map((reason, i) => (
                                  <li key={i}>{reason}</li>
                                ))}
                              </ul>
                            </div>

                            <div>
                              <h5 className="font-semibold">Business Impact</h5>
                              <ul className="list-disc ml-6 mt-2 space-y-1 text-sm">
                                {config.impact.map((impact, i) => (
                                  <li key={i}>{impact}</li>
                                ))}
                              </ul>
                            </div>

                          </div>
                        );
                      })}

                    </div>
                  ) : (
                    <div className="mt-6 rounded-xl border border-green-200 bg-green-50 p-5">
                      <h3 className="text-lg font-bold text-green-700">
                        ✅ Performing Well
                      </h3>
                      <p className="text-sm text-green-800 mt-1">
                        All evaluation parameters are at or above the {ATTENTION_THRESHOLD}/100 threshold. No action required.
                      </p>
                    </div>
                  )}

                </div>

                <span
                  className={`ml-3 shrink-0 px-3 py-1 rounded-full text-xs font-bold ${
                    activity.priority === 'High'
                      ? 'bg-red-100 text-red-700'
                      : activity.priority === 'Medium'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-green-100 text-green-700'
                  }`}
                >
                  {activity.priority}
                </span>

              </div>

              <div className="mt-4">
                <button
                  onClick={() =>
                    navigate(
                      `/vendor-management/risk/${encodeURIComponent(activity.vendorName)}`
                    )
                  }
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  View Vendor
                </button>
              </div>

            </div>
          ))}
        </div>
      )}
    </div>
  );
}
